import {
	adjustInbox,
	deleteAllNotifications,
	deleteNotification,
	getInbox,
	setNotificationCounter,
	updateNotificationInboxStatus,
} from "@actions/profile.js";
import { openNotification } from "@actions/websocket.js";
import { showAlert } from "@components/alert/action.js";
import { showBettoAlert } from "@layouts/betto/components/Alert/showBettoAlert";
import { useVerification } from "@layouts/betto/verificationPopup/index.js";
import { normalizeNotificationMessageLinks } from "@utils";
import {
	applyNotificationReadState,
	getNotificationId,
	getNotificationReadSets,
	getUnreadCountFromStorage,
	isNotificationUnread,
	normalizeNotificationSubmissionTime,
	persistNotificationReadSets,
	syncNotificationsWithStorage,
	updateNotificationReadStateInStorage,
	filterValidNotifications,
} from "@utils/notifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const PAGE_SIZE = 50;

const isElement = (node) => node instanceof Element;

const buildInboxSignature = (items) =>
	items?.length ? items.map((item) => `${item.submissionTime}:${item.isNew}`).join("|") : "";

const areNotificationListsEqual = (next, current) => {
	if (next.length !== current.length) {
		return false;
	}

	return next.every(
		(item, index) =>
			item.submissionTime === current[index]?.submissionTime &&
			String(item.isNew) === String(current[index]?.isNew)
	);
};

const deriveNotificationsFromInbox = (inboxItems) => {
	const uniqueNotifications = [
		...new Map(
			filterValidNotifications(inboxItems).map((item) => [item.submissionTime, { ...item }])
		).values(),
	];

	const synced = syncNotificationsWithStorage(uniqueNotifications);

	return applyNotificationReadState(synced);
};

const selectProfileInbox = (state) => state.profile.inbox;
const selectProfileInboxLoading = (state) => state.profile.inboxLoading;

export const useInbox = (location) => {
	const dispatch = useDispatch();
	const inbox = useSelector(selectProfileInbox);
	const loading = useSelector(selectProfileInboxLoading);
	const loadingRef = useRef(null);
	const prevYRef = useRef(0);
	const openedMessageIdRef = useRef(null);
	const inboxSignatureRef = useRef("");
	const notificationsRef = useRef([]);
	const loadingStateRef = useRef(loading);
	const inboxOffsetRef = useRef(inbox?.offset);
	const { withVerification } = useVerification();

	const [activeItemIndex, setActiveItemIndex] = useState(-1);
	const [allRead, setAllRead] = useState(true);
	const [notifications, setNotifications] = useState([]);
	const [sentinelElement, setSentinelElement] = useState(null);
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [isSelectionMode, setIsSelectionMode] = useState(false);

	loadingStateRef.current = loading;
	inboxOffsetRef.current = inbox?.offset;

	const setLoadingRef = useCallback((node) => {
		loadingRef.current = node;
		setSentinelElement(isElement(node) ? node : null);
	}, []);

	const toggleReadStatus = useCallback(
		(item, index, { skipWebsocket = false } = {}) => {
			setNotifications((currentNotifications) => {
				const nextNotifications = currentNotifications.slice();
				const itemToModify = { ...nextNotifications[index] };

				if (!itemToModify.submissionTime) {
					return currentNotifications;
				}

				const isMarkingAsRead = isNotificationUnread(itemToModify);
				const notificationId = getNotificationId(itemToModify);

				itemToModify.isNew = isMarkingAsRead ? "false" : "true";
				nextNotifications[index] = itemToModify;

				updateNotificationReadStateInStorage(itemToModify.submissionTime, !isMarkingAsRead);

				const nextAllRead = nextNotifications.every(
					(notification) => !isNotificationUnread(notification)
				);

				setAllRead(nextAllRead);
				notificationsRef.current = nextNotifications;
				dispatch(adjustInbox(nextNotifications));
				dispatch(setNotificationCounter(getUnreadCountFromStorage()));

				if (notificationId) {
					dispatch(
						updateNotificationInboxStatus(
							isMarkingAsRead
								? { readNotificationIds: [notificationId] }
								: { unreadNotificationIds: [notificationId] }
						)
					);
				}

				if (!skipWebsocket && isMarkingAsRead) {
					dispatch(
						openNotification(
							itemToModify.tenant,
							itemToModify.username,
							itemToModify.time
						)
					);
				}

				return nextNotifications;
			});
		},
		[dispatch]
	);

	const manualVerificationRequest = useCallback(
		async (event) => {
			event.preventDefault();
			const username = JSON.parse(localStorage.getItem("user_data") || "{}")?.username;

			if (withVerification && username) {
				try {
					await withVerification(() =>
						Promise.resolve({ username, manuallink: event.target.href })
					);

					showBettoAlert(
						(alertPayload) => dispatch(showAlert(alertPayload)),
						"verification-complete"
					);
				} catch (error) {
					if (error.message.includes("verification.closed")) {
						return;
					}

					showBettoAlert(
						(alertPayload) => dispatch(showAlert(alertPayload)),
						"invalid-verification-code"
					);
				}
			} else {
				showBettoAlert(
					(alertPayload) => dispatch(showAlert(alertPayload)),
					"invalid-verification-code",
					{
						msg: "VERIFICATION_LINK_BROKEN",
					}
				);
			}
		},
		[dispatch, withVerification]
	);

	useEffect(() => {
		dispatch(getInbox({ size: PAGE_SIZE }));
	}, [dispatch]);

	useEffect(() => {
		notificationsRef.current = notifications;
	}, [notifications]);

	useEffect(() => {
		const inboxItems = inbox?.notifications;

		if (!inboxItems?.length) {
			inboxSignatureRef.current = "";

			if (notificationsRef.current.length > 0) {
				notificationsRef.current = [];
				setNotifications([]);
				setAllRead(true);
			}

			return;
		}

		const signature = buildInboxSignature(inboxItems);

		if (signature === inboxSignatureRef.current) {
			return;
		}

		inboxSignatureRef.current = signature;

		const withState = deriveNotificationsFromInbox(inboxItems);

		if (!areNotificationListsEqual(withState, notificationsRef.current)) {
			notificationsRef.current = withState;
			setNotifications(withState);
			setAllRead(withState.every((item) => !isNotificationUnread(item)));
		}

		setSelectedIds((current) => {
			const next = new Set();
			withState.forEach((item) => {
				if (current.has(item.submissionTime)) {
					next.add(item.submissionTime);
				}
			});
			return next;
		});

		dispatch(adjustInbox(withState));
		dispatch(setNotificationCounter(getUnreadCountFromStorage()));
	}, [inbox?.notifications, dispatch]);

	useEffect(() => {
		const params = new URLSearchParams(location?.search || "");
		const messageId = params.get("messageId");

		if (!messageId || !notifications.length || openedMessageIdRef.current === messageId) {
			return;
		}

		const index = notifications.findIndex((item) => item.submissionTime === messageId);

		if (index === -1) {
			return;
		}

		openedMessageIdRef.current = messageId;
		setActiveItemIndex(index);

		if (isNotificationUnread(notifications[index])) {
			toggleReadStatus(notifications[index], index);
		}
	}, [location?.search, notifications, toggleReadStatus]);

	useEffect(() => {
		if (!sentinelElement) {
			return undefined;
		}

		const handlePagination = (entries) => {
			const y = entries[0]?.boundingClientRect?.y ?? 0;
			const offset = inboxOffsetRef.current;

			if (prevYRef.current > y && offset && !loadingStateRef.current) {
				dispatch(getInbox({ size: PAGE_SIZE, offset }));
			}

			if (y > prevYRef.current) {
				prevYRef.current = y;
			}
		};

		const observer = new IntersectionObserver(handlePagination, {
			root: null,
			rootMargin: "0px",
			threshold: 1,
		});

		observer.observe(sentinelElement);

		return () => observer.disconnect();
	}, [dispatch, sentinelElement]);

	useEffect(() => {
		const links = document.querySelectorAll(".verification-link");
		const handlers = Array.from(links).map((link) => {
			link.addEventListener("click", manualVerificationRequest);
			return link;
		});

		return () => {
			handlers.forEach((link) => {
				link.removeEventListener("click", manualVerificationRequest);
			});
		};
	}, [notifications, activeItemIndex, manualVerificationRequest]);

	const handleCardClick = useCallback(
		(item, index) => {
			if (isSelectionMode) {
				setSelectedIds((prev) => {
					const next = new Set(prev);
					if (next.has(item.submissionTime)) {
						next.delete(item.submissionTime);
					} else {
						next.add(item.submissionTime);
					}
					return next;
				});
				return;
			}

			if (isNotificationUnread(item)) {
				toggleReadStatus(item, index);
			}

			setActiveItemIndex((current) => (current === index ? -1 : index));
		},
		[isSelectionMode, toggleReadStatus]
	);

	const handleToggleRead = useCallback(
		(event, item, index) => {
			event.stopPropagation();
			toggleReadStatus(item, index, { skipWebsocket: true });
		},
		[toggleReadStatus]
	);

	const handleDelete = useCallback(
		(event, item, index) => {
			event.stopPropagation();

			if (!item?.submissionTime) {
				return;
			}

			dispatch(
				showAlert({
					title: "DELETE_NOTIFICATION_CONFIRM_TITLE",
					msg: "DELETE_NOTIFICATION_CONFIRM_MSG",
					isConfirm: true,
					persistent: true,
					acceptCallback: () => {
						const nextNotifications = notificationsRef.current.filter(
							(notification) => notification.submissionTime !== item.submissionTime
						);

						const { isNew, notNew } = getNotificationReadSets();
						const submissionTime = normalizeNotificationSubmissionTime(
							item.submissionTime
						);
						isNew.delete(submissionTime);
						notNew.delete(submissionTime);
						persistNotificationReadSets(isNew, notNew);

						inboxSignatureRef.current = buildInboxSignature(nextNotifications);
						notificationsRef.current = nextNotifications;
						setNotifications(nextNotifications);
						setAllRead(
							nextNotifications.every(
								(notification) => !isNotificationUnread(notification)
							)
						);

						setActiveItemIndex((current) => {
							if (current === index) {
								return -1;
							}

							if (current > index) {
								return current - 1;
							}

							return current;
						});

						dispatch(adjustInbox(nextNotifications));
						dispatch(setNotificationCounter(getUnreadCountFromStorage()));
						dispatch(deleteNotification(item.submissionTime));
					},
				})
			);
		},
		[dispatch]
	);

	const handleDeleteAll = useCallback(() => {
		dispatch(
			showAlert({
				title: "DELETE_ALL_NOTIFICATIONS_CONFIRM_TITLE",
				msg: "DELETE_ALL_NOTIFICATIONS_CONFIRM_MSG",
				isConfirm: true,
				persistent: true,
				acceptCallback: () => {
					setNotifications([]);
					setAllRead(true);
					inboxSignatureRef.current = "";
					notificationsRef.current = [];
					setActiveItemIndex(-1);

					const { isNew, notNew } = getNotificationReadSets();
					isNew.clear();
					notNew.clear();
					persistNotificationReadSets(isNew, notNew);

					dispatch(adjustInbox([]));
					dispatch(setNotificationCounter(0));
					dispatch(deleteAllNotifications());
				},
			})
		);
	}, [dispatch]);

	const markAllAsRead = useCallback(() => {
		const readNotificationIds = notifications
			.filter((item) => isNotificationUnread(item))
			.map(getNotificationId)
			.filter(Boolean);

		const nextNotifications = notifications.map((item) => {
			if (item.submissionTime) {
				updateNotificationReadStateInStorage(item.submissionTime, false);
			}

			return { ...item, isNew: "false" };
		});

		notificationsRef.current = nextNotifications;
		setNotifications(nextNotifications);
		setAllRead(true);
		dispatch(adjustInbox(nextNotifications));
		dispatch(setNotificationCounter(0));

		if (readNotificationIds.length > 0) {
			dispatch(updateNotificationInboxStatus({ readNotificationIds }));
		}
	}, [notifications, dispatch]);

	const handleToggleSelect = useCallback((event, submissionTime) => {
		event.stopPropagation();
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(submissionTime)) {
				next.delete(submissionTime);
			} else {
				next.add(submissionTime);
			}
			return next;
		});
	}, []);

	const handleSelectAll = useCallback(() => {
		const allCurrentTimes = notificationsRef.current.map((n) => n.submissionTime);
		const allSelected =
			allCurrentTimes.length > 0 && allCurrentTimes.every((time) => selectedIds.has(time));

		if (allSelected) {
			setSelectedIds(new Set());
			setIsSelectionMode(false);
			return;
		}

		setSelectedIds(new Set(allCurrentTimes));
		setIsSelectionMode(true);
	}, [selectedIds]);

	const handleMarkSelectedAsRead = useCallback(() => {
		if (selectedIds.size === 0) return;

		const readNotificationIds = notificationsRef.current
			.filter((item) => selectedIds.has(item.submissionTime) && isNotificationUnread(item))
			.map(getNotificationId)
			.filter(Boolean);

		setNotifications((currentNotifications) => {
			const nextNotifications = currentNotifications.map((item) => {
				if (selectedIds.has(item.submissionTime)) {
					if (item.submissionTime) {
						updateNotificationReadStateInStorage(item.submissionTime, false);
					}
					return { ...item, isNew: "false" };
				}
				return item;
			});

			const nextAllRead = nextNotifications.every(
				(notification) => !isNotificationUnread(notification)
			);

			setAllRead(nextAllRead);
			notificationsRef.current = nextNotifications;
			dispatch(adjustInbox(nextNotifications));
			dispatch(setNotificationCounter(getUnreadCountFromStorage()));

			return nextNotifications;
		});

		if (readNotificationIds.length > 0) {
			dispatch(updateNotificationInboxStatus({ readNotificationIds }));
		}

		setSelectedIds(new Set());
		setIsSelectionMode(false);
	}, [selectedIds, dispatch]);

	const handleDeleteSelected = useCallback(() => {
		if (selectedIds.size === 0) return;

		dispatch(
			showAlert({
				title: "DELETE_SELECTED_NOTIFICATIONS_CONFIRM_TITLE",
				msg: "DELETE_SELECTED_NOTIFICATIONS_CONFIRM_MSG",
				isConfirm: true,
				persistent: true,
				acceptCallback: () => {
					const nextNotifications = notificationsRef.current.filter(
						(notification) => !selectedIds.has(notification.submissionTime)
					);

					const { isNew, notNew } = getNotificationReadSets();
					selectedIds.forEach((submissionTime) => {
						const normTime = normalizeNotificationSubmissionTime(submissionTime);
						isNew.delete(normTime);
						notNew.delete(normTime);
					});
					persistNotificationReadSets(isNew, notNew);

					inboxSignatureRef.current = buildInboxSignature(nextNotifications);
					notificationsRef.current = nextNotifications;
					setNotifications(nextNotifications);
					setAllRead(
						nextNotifications.every(
							(notification) => !isNotificationUnread(notification)
						)
					);

					setActiveItemIndex(-1);

					dispatch(adjustInbox(nextNotifications));
					dispatch(setNotificationCounter(getUnreadCountFromStorage()));

					selectedIds.forEach((submissionTime) => {
						dispatch(deleteNotification(submissionTime));
					});

					setSelectedIds(new Set());
					setIsSelectionMode(false);
				},
			})
		);
	}, [selectedIds, dispatch]);

	return {
		notifications,
		loading,
		loadingRef: setLoadingRef,
		activeItemIndex,
		allRead,
		selectedIds,
		isSelectionMode,
		handleCardClick,
		handleToggleRead,
		handleDelete,
		handleDeleteAll,
		markAllAsRead,
		handleToggleSelect,
		handleSelectAll,
		handleMarkSelectedAsRead,
		handleDeleteSelected,
		normalizeMessage: normalizeNotificationMessageLinks,
	};
};
